import React from "react";
import {
  Grid,
  Typography,
  Divider,
  Checkbox,
  FormControl,
  FormControlLabel,
  Box,
} from "@mui/material";
import { Controller, UseFormReturn } from "react-hook-form";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import SkeletonWrapper from "../Skeleton/SkeletonWrapper";
import { CustomSelect } from "../CustomFields/CustomSelect";
import CustomTimePicker from "../TimePicker/CustomTimePicker";
import dayjs, { Dayjs } from "dayjs";

// ---------- Type Definitions ----------

// Location option type
interface LocationOption {
  value: string;
  label: string;
  locationId?: string;
  code?: string;
}

// Availability detail row
interface AvailabilityDetail {
  dayName: string;
  startTime: string | Date | null;
  endTime: string | Date | null;
  isDeleted?: boolean;
  displayOrder: number;
}

// Availability data in form
interface AvailabilityData {
  [key: string]: any; // for dynamic keys like isMondayEnable, etc.
  availabilityDetailColl: AvailabilityDetail[];
}

// Props for WorkingHoursSection
interface WorkingHoursSectionProps {
  methods: UseFormReturn<{ availabilityData: AvailabilityData }>;
  showSkeleton: boolean;
  locationData: LocationOption[];
  daysOfWeek: string[];
  filteredAvailabilityDetailColl: AvailabilityDetail[];
  handleToggleDay: (day: string, checked: boolean) => void;
  handleAddRow: (day: string, startTime: Date, endTime: Date) => void;
  handleDetailFieldChange: (
    day: string,
    displayOrder: number,
    field: keyof AvailabilityDetail
  ) => void;
}

// ---------- Component ----------
const WorkingHoursSection: React.FC<WorkingHoursSectionProps> = ({
  methods,
  showSkeleton,
  locationData,
  daysOfWeek,
  filteredAvailabilityDetailColl,
  handleToggleDay,
  handleAddRow,
  handleDetailFieldChange,
}) => (
  <>
    <Grid item xs={12}>
      <Typography variant="h6">Routine Working Hours</Typography>
      <Typography className="font-size-12">
        Set when you’re regularly available for appointments
      </Typography>
    </Grid>

    {/* Location select */}
    <Grid item xs={12} sm={6} md={3}>
      <FormControl fullWidth>
        <SkeletonWrapper isLoading={showSkeleton} className="skeleton-input">
          <Controller
            control={methods.control}
            name="availabilityData.locationId"
            render={({ field }) => (
              <CustomSelect
                {...field}
                isNotCreatable
                id="availabilityData.locationId"
                FieldName="availabilityData.locationId"
                options={locationData}
                isDisabled={true}
                handleChange={(e: LocationOption | null) => {
                  methods.setValue("availabilityData.locationId", e || null, {
                    shouldValidate: true,
                  });

                  methods.setValue(
                    "availabilityData.locationName",
                    e?.label || null,
                    {
                      shouldValidate: true,
                    }
                  );
                }}
              />
            )}
          />
        </SkeletonWrapper>
      </FormControl>
    </Grid>

    <Grid item xs={12}>
      {daysOfWeek.map((dayName) => {
        const isEnabled =
          methods.getValues()?.availabilityData?.[`is${dayName}Enable`];

        return (
          <Grid container mt={1} key={dayName}>
            <Grid item xs={12} sm={3} md={2}>
              <FormControlLabel
                control={
                  <SkeletonWrapper
                    isLoading={showSkeleton}
                    className="check-box"
                  >
                    <Checkbox
                      {...methods.register(
                        `availabilityData.is${dayName}Enable`,
                        {
                          onChange: (e) =>
                            handleToggleDay(
                              dayName.toUpperCase(),
                              e.target.checked
                            ),
                        }
                      )}
                      checked={isEnabled ?? false}
                    />
                  </SkeletonWrapper>
                }
                label={dayName}
              />
            </Grid>

            <Grid item xs={12} sm={9} md={8} lg={6}>
              <Box sx={{ width: "100%" }}>
                {isEnabled ? (
                  filteredAvailabilityDetailColl
                    .filter(
                      (x) => !x.isDeleted && x.dayName === dayName.toUpperCase()
                    )
                    .map((row, index, arr) => (
                      <Box
                        key={row.dayName + row.displayOrder}
                        sx={{
                          width: "100%",
                          mb: 1,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {/* Start Time */}
                        <FormControl sx={{ mr: 1 }}>
                          <SkeletonWrapper
                            isLoading={showSkeleton}
                            className="skeleton-input"
                          >
                            <Controller
                              control={methods.control}
                              name={`availabilityData.availabilityDetailColl.${row.displayOrder}.startTime`}
                              render={({ field }) => (
                                <CustomTimePicker
                                  {...field}
                                  value={
                                    row?.startTime
                                      ? dayjs(new Date(row.startTime))
                                      : null
                                  }
                                  onTimeChange={(newTime: Dayjs | null) => {
                                    methods.setValue(
                                      `availabilityData.availabilityDetailColl.${row.displayOrder}.startTime`,
                                      newTime,
                                      { shouldValidate: true }
                                    );
                                  }}
                                />
                              )}
                            />
                          </SkeletonWrapper>
                        </FormControl>

                        {/* End Time */}
                        <FormControl sx={{ mr: 1 }}>
                          <SkeletonWrapper
                            isLoading={showSkeleton}
                            className="skeleton-input"
                          >
                            <Controller
                              control={methods.control}
                              name={`availabilityData.availabilityDetailColl.${row.displayOrder}.endTime`}
                              render={({ field }) => (
                                <CustomTimePicker
                                  {...field}
                                  value={
                                    row?.endTime
                                      ? dayjs(new Date(row.endTime))
                                      : null
                                  }
                                  onTimeChange={(newTime: Dayjs | null) => {
                                    methods.setValue(
                                      `availabilityData.availabilityDetailColl.${row.displayOrder}.endTime`,
                                      newTime,
                                      { shouldValidate: true }
                                    );
                                  }}
                                />
                              )}
                            />
                          </SkeletonWrapper>
                        </FormControl>

                        {arr.length > 1 && (
                          <ClearIcon
                            onClick={() =>
                              handleDetailFieldChange(
                                dayName.toUpperCase(),
                                row.displayOrder,
                                "isDeleted"
                              )
                            }
                            sx={{ color: "red", cursor: "pointer", mr: 1 }}
                          />
                        )}

                        {index === 0 && (
                          <AddIcon
                            onClick={() =>
                              handleAddRow(
                                dayName.toUpperCase(),
                                new Date(
                                  dayjs().format("YYYY-MM-DD") + "T17:00"
                                ),
                                new Date(
                                  dayjs().format("YYYY-MM-DD") + "T20:00"
                                )
                              )
                            }
                            sx={{ cursor: "pointer", color: "primary.main" }}
                          />
                        )}
                      </Box>
                    ))
                ) : (
                  <Typography
                    sx={{ mt: "10px" }}
                    className="font-color-grey-600"
                  >
                    Unavailable
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        );
      })}
    </Grid>
  </>
);

export default WorkingHoursSection;
